import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, updateProfile } from 'firebase/auth';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { auth, storage, db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/firebase/useAuth';
import { useLang } from '../../core/i18n';
import ProductCard from '../../components/ProductCard';
import { IconCamera, IconArrowLeft, IconSettings } from '../../components/Icons';

export default function ProfilePage() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const navigate  = useNavigate();
  const fileRef   = useRef(null);

  const [photoURL,   setPhotoURL]   = useState('');
  const [uploading,  setUploading]  = useState(false);
  const [userDoc,    setUserDoc]    = useState(null);
  const [stats,      setStats]      = useState({ products: 0, followers: 0, following: 0 });
  const [myProducts, setMyProducts] = useState([]);

  useEffect(() => {
    if (!user) return;
    setPhotoURL(user.photoURL || '');

    // User profile document
    getDoc(doc(db, 'users', user.uid))
      .then(snap => { if (snap.exists()) setUserDoc(snap.data()); })
      .catch(() => {});

    // My products (supports both uid and userId field names)
    Promise.all([
      getDocs(query(collection(db, 'user_products'), where('uid',    '==', user.uid))),
      getDocs(query(collection(db, 'user_products'), where('userId', '==', user.uid))),
    ]).then(([snapA, snapB]) => {
      const seen = new Set();
      const prods = [...snapA.docs, ...snapB.docs]
        .filter(d => { if (seen.has(d.id)) return false; seen.add(d.id); return true; })
        .map(d => ({ id: d.id, ...d.data(), _col: 'user_products', isCreator: true }));
      setMyProducts(prods);
      setStats(prev => ({ ...prev, products: prods.length }));
    }).catch(() => {});

    // Followers (people who follow this user)
    getDocs(query(collection(db, 'subscriptions'), where('followingId', '==', user.uid)))
      .then(s => setStats(prev => ({ ...prev, followers: s.size }))).catch(() => {});

    // Following (people this user follows)
    getDocs(query(collection(db, 'subscriptions'), where('followerId', '==', user.uid)))
      .then(s => setStats(prev => ({ ...prev, following: s.size }))).catch(() => {});
  }, [user]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;
    setUploading(true);
    try {
      const path = `profile_photos/${user.uid}/avatar`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url  = await getDownloadURL(snap.ref);
      await updateProfile(auth.currentUser, { photoURL: url });
      setPhotoURL(url);
    } catch (err) { console.error(err); }
    finally { setUploading(false); }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    sessionStorage.removeItem('auth_modal_seen');
    navigate('/');
  };

  if (loading) return <div style={s.page}><div style={s.spinner} /></div>;

  if (!user) return (
    <div style={s.guestWrap}>
      <div style={s.lockBox}><img src="/og-image.png" alt="Nexsap" style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover' }} /></div>
      <h2 style={s.lockTitle}>Connectez-vous</h2>
      <p style={s.lockSub}>Accédez à votre profil Nexsap</p>
      <button style={s.btnPrimary} onClick={() => navigate('/login')}>{t.login}</button>
    </div>
  );

  const username = userDoc?.username || user.displayName || user.email?.split('@')[0] || 'user';
  const initials = username[0]?.toUpperCase() || '?';

  return (
    <div style={s.page}>

      {/* ── Dark profile header ── */}
      <div style={s.darkSection}>

        {/* Internal header row */}
        <div style={s.topRow}>
          <button style={s.circleBtn} onClick={() => navigate(-1)}>
            <IconArrowLeft size={18} color="#fff" />
          </button>
          <span style={s.topTitle}>@{username}</span>
          <button style={s.circleBtn} onClick={() => navigate('/settings')}>
            <IconSettings size={18} color="#fff" />
          </button>
        </div>

        {/* Avatar */}
        <div style={s.avatarWrap}>
          <div style={s.avatarRing}>
            {photoURL
              ? <img src={photoURL} alt="avatar" style={s.avatarImg} />
              : <span style={s.avatarInitials}>{initials}</span>
            }
          </div>
          <button style={s.cameraBtn} onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? <div style={s.miniSpinner} /> : <IconCamera size={13} color="#fff" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
        </div>

        <p style={s.username}>@{username}</p>

        {/* Stats card */}
        <div style={s.statsCard}>
          <StatBox value={stats.products}  label="Produits" />
          <div style={s.statDivider} />
          <StatBox value={stats.followers} label="Abonnés" />
          <div style={s.statDivider} />
          <StatBox value={stats.following} label="Suit" />
        </div>

        {/* Edit button */}
        <button style={s.editBtn} onClick={() => navigate('/settings')}>
          <span style={{ fontSize: 14 }}>✏️</span> Edit
        </button>
      </div>

      {/* ── Products section ── */}
      <div style={s.section}>
        <div style={s.sectionHeader}>
          <div style={s.sectionIcon}>🛍️</div>
          <span style={s.sectionTitle}>Produits</span>
          {myProducts.length > 0 && (
            <div style={s.badge}>{myProducts.length}</div>
          )}
        </div>

        {myProducts.length === 0 ? (
          <div style={s.emptyBox}>
            <span style={{ fontSize: 36 }}>📦</span>
            <p style={s.emptyText}>Aucun produit publié</p>
            <button style={s.btnCreate} onClick={() => navigate('/creer')}>
              + Publier un produit
            </button>
          </div>
        ) : (
          <div style={s.grid}>
            {myProducts.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>

      {/* ── Sign out ── */}
      <div style={s.signOutWrap}>
        <button style={s.btnDanger} onClick={handleSignOut}>{t.logout}</button>
      </div>

    </div>
  );
}

function StatBox({ value, label }) {
  return (
    <div style={s.statBox}>
      <span style={s.statNum}>{value}</span>
      <span style={s.statLabel}>{label}</span>
    </div>
  );
}

const s = {
  page         : { background: '#f7f8fa', minHeight: '100vh', paddingBottom: 100 },
  spinner      : { width: 36, height: 36, border: '3px solid #eee', borderTopColor: '#4361ee', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '40vh auto 0' },

  // Guest
  guestWrap    : { background: '#f7f8fa', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' },
  lockBox      : { background: '#eef0ff', borderRadius: 20, width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  lockTitle    : { color: '#1a1a1a', fontSize: 22, fontWeight: 700, marginBottom: 8 },
  lockSub      : { color: '#888', fontSize: 14, marginBottom: 32, textAlign: 'center', maxWidth: 280 },
  btnPrimary   : { background: '#4361ee', color: '#fff', border: 'none', borderRadius: 14, height: 52, width: '100%', maxWidth: 320, fontSize: 16, fontWeight: 600, cursor: 'pointer' },

  // Dark header
  darkSection  : { background: 'linear-gradient(180deg, #0d0d14 0%, #1a1a2e 100%)', padding: '0 16px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  topRow       : { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingTop: 14, paddingBottom: 20 },
  topTitle     : { color: '#fff', fontSize: 16, fontWeight: 700 },
  circleBtn    : { width: 36, height: 36, borderRadius: 18, background: 'rgba(255,255,255,0.12)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },

  avatarWrap   : { position: 'relative', marginBottom: 12 },
  avatarRing   : { width: 90, height: 90, borderRadius: 45, border: '3px solid #fff', background: '#1e1e3f', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  avatarImg    : { width: '100%', height: '100%', objectFit: 'cover' },
  avatarInitials:{ fontSize: 36, fontWeight: 700, color: '#4361ee' },
  cameraBtn    : { position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, borderRadius: 13, background: '#4361ee', border: '2px solid #0d0d14', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  miniSpinner  : { width: 10, height: 10, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },

  username     : { color: '#fff', fontSize: 16, fontWeight: 700, marginBottom: 20 },

  statsCard    : { display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.08)', borderRadius: 18, padding: '14px 8px', width: '100%', maxWidth: 340, marginBottom: 18 },
  statBox      : { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 },
  statNum      : { color: '#fff', fontSize: 20, fontWeight: 700 },
  statLabel    : { color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 500 },
  statDivider  : { width: 1, height: 32, background: 'rgba(255,255,255,0.15)' },

  editBtn      : { display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, #4361ee 0%, #6a82fb 100%)', color: '#fff', border: 'none', borderRadius: 14, height: 46, paddingLeft: 32, paddingRight: 32, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(67,97,238,0.4)' },

  // Products section
  section      : { padding: '20px 16px 0' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  sectionIcon  : { fontSize: 20 },
  sectionTitle : { fontSize: 16, fontWeight: 700, color: '#1a1a1a', flex: 1 },
  badge        : { background: '#4361ee', color: '#fff', fontSize: 12, fontWeight: 700, borderRadius: 12, padding: '3px 10px', minWidth: 24, textAlign: 'center' },

  emptyBox     : { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 0' },
  emptyText    : { color: '#aaa', fontSize: 14, margin: 0 },
  btnCreate    : { background: '#4361ee', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 },

  grid         : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 },

  signOutWrap  : { padding: '24px 16px 0' },
  btnDanger    : { width: '100%', background: '#fff5f5', color: '#e74c3c', border: '1.5px solid #ffd5d5', borderRadius: 14, height: 50, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
};
